import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import type { ToggleConfirmationContentProps, ToggleWrapperProps } from "../types/toggleType";

import { closeModal, openModal } from "../redux/reducers/uiSlice";
import { ToggleColor } from "../types/toggleType";
import { Button } from "./Button";

const toneMap: Record<ToggleColor, { active: string; focus: string; confirmBtn: string }> = {
  green: {
    active: "bg-[#34C759]",
    focus: "focus-visible:ring-[#34C759]",
    confirmBtn: "bg-emerald-600 hover:bg-emerald-700 text-white",
  },
  blue: {
    active: "bg-[#0A84FF]",
    focus: "focus-visible:ring-[#0A84FF]",
    confirmBtn: "bg-blue-600 hover:bg-blue-700 text-white",
  },
};

function ToggleConfirmationContent({
  description,
  confirmText,
  cancelText,
  confirmBtnClassName,
  onConfirm,
  onCancel,
}: ToggleConfirmationContentProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleConfirmClick = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      await onConfirm();
    } catch (error: any) {
      const fallback = "Failed to update status. Please try again.";
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        fallback;
      setErrorMessage(String(apiMessage));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-5 p-1">
      <p className="text-sm text-theme-text-muted">{description}</p>

      {errorMessage ? (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
          {errorMessage}
        </p>
      ) : null}

      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="secondary"
          disabled={isSubmitting}
          onClick={onCancel}
          className="min-h-auto rounded-lg px-4 py-2 text-sm"
        >
          {cancelText}
        </Button>

        <Button
          type="button"
          disabled={isSubmitting}
          onClick={handleConfirmClick}
          className={`min-h-auto rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-60 ${confirmBtnClassName}`}
        >
          {isSubmitting ? "Please wait..." : confirmText}
        </Button>
      </div>
    </div>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
  toggleKey,
  disabled = false,
  color = "green",
  ariaLabel = "Toggle value",
  isConformation,
  confirmationTitle = "Confirm status change",
  confirmationDescription = "Are you sure you want to change this status?",
  confirmText = "Yes",
  cancelText = "No",
  onSuccess,
  onCancel,
}: ToggleWrapperProps) {
  const dispatch = useDispatch();

  const shouldConfirm = Boolean(isConformation);
  const tones = useMemo(() => toneMap[color], [color]);

  const triggerChange = async (nextValue: boolean) => {
    await onChange(nextValue, toggleKey);
    if (onSuccess) {
      await onSuccess(nextValue, toggleKey);
    }
  };

  const handleToggleClick = () => {
    if (disabled) return;

    const nextValue = !checked;

    if (shouldConfirm) {
      dispatch(
        openModal({
          name: "toggle-confirmation",
          status: "modal",
          title: confirmationTitle,
          className: "max-w-md",
          outerClassName: "items-center",
          children: (
            <ToggleConfirmationContent
              description={confirmationDescription}
              confirmText={confirmText}
              cancelText={cancelText}
              confirmBtnClassName={tones.confirmBtn}
              onCancel={handleCancel}
              onConfirm={async () => {
                await triggerChange(nextValue);
                dispatch(closeModal());
              }}
            />
          ),
        })
      );
      return;
    }

    void triggerChange(nextValue);
  };

  const handleCancel = () => {
    dispatch(closeModal());
    onCancel?.();
  };

  return (
    <>
      <div className="inline-flex items-center gap-2">
        {label ? <span>{label}</span> : null}

        <button
          type="button"
          role="switch"
          aria-checked={checked}
          aria-label={ariaLabel}
          disabled={disabled}
          onClick={handleToggleClick}
          className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full p-0.5 transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${tones.focus} ${checked ? tones.active : "bg-[#E5E5EA]"}`}
        >
          <span className="sr-only">Toggle value</span>
          <span
            aria-hidden="true"
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.25)] transition duration-200 ease-in-out ${checked ? "translate-x-5" : "translate-x-0"}`}
          />
        </button>
      </div>
    </>
  );
}

export default Toggle;
